import { Zap, Search, BarChart2, Lightbulb, Users, Globe, GraduationCap, Layers, Shield, Target, TrendingUp, Bot, MapPin, BookOpen, Cpu, Star, ScanSearch, Workflow } from "lucide-react";
import ArabicToggle from "@/components/ArabicToggle";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqAccordion from "@/components/FaqAccordion";

import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";
import SchemaScript from "@/components/SchemaScript";
const services = [
  // Core AI Services
  {
    category: "Core AI Services",
    color: "#FF8C00",
    items: [
      {
        label: "Agentic AI Marketing",
        href: "/services/gemini-agentic-ai/",
        desc: "AI-powered lead generation systems deployed and live in 30 days. Autonomous agents handle prospecting, nurturing, and qualification at scale.",
        icon: Zap,
        badge: "Most Popular",
      },
      {
        label: "SEO, AEO & GEO",
        href: "/services/seo-aeo-geo/",
        desc: "Get cited in ChatGPT, Perplexity, Google AI Overviews, and Gemini. Structured content and schema that wins AI citations, not just rankings.",
        icon: Search,
        badge: null,
      },
      {
        label: "AI Visibility Audit",
        href: "/pricing/",
        desc: "A paid, senior-led diagnosis of AI-answer visibility, priority pages and the next remediation steps.",
        icon: ScanSearch,
        badge: "New",
      },
      {
        label: "Get Cited by ChatGPT & Gemini",
        href: "/services/ai-citation-service/",
        desc: "Evidence-led source readiness for AI answers, with a defined audit and monthly scope. No citation guarantees.",
        icon: Search,
        badge: "New",
      },
      {
        label: "AI Citation Programme",
        href: "/services/ai-citation-programme/",
        desc: "Senior-led source-ready page, evidence and measurement programme for priority AI-search questions.",
        icon: Search,
        badge: "New",
      },
      {
        label: "AI Marketing Agency for Ecommerce",
        href: "/services/ai-marketing-agency-for-ecommerce/",
        desc: "Google Shopping, search, Performance Max and paid social organised around product evidence and measurement.",
        icon: BarChart2,
        badge: "New",
      },
      {
        label: "PPC & Performance Max",
        href: "/services/ppc-performance-max/",
        desc: "ROAS-led Google Ads and Performance Max campaigns. Full-funnel attribution from first click to closed deal.",
        icon: BarChart2,
        badge: null,
      },
      {
        label: "AI Growth Strategy",
        href: "/services/ai-marketing-strategy/",
        desc: "Go-to-market systems designed and deployed, not just planned. ICP definition, buyer journey mapping, and AI-native GTM execution.",
        icon: Lightbulb,
        badge: null,
      },
      {
        label: "Account-Based Marketing",
        href: "/services/account-based-marketing/",
        desc: "Buying committee targeting for complex B2B. Multi-thread engagement across all stakeholders in your target accounts.",
        icon: Users,
        badge: null,
      },
    ],
  },
  // Specialist Services
  {
    category: "Specialist Services",
    color: "#00D4AA",
    items: [
      {
        label: "Integrated Marketing Communications",
        href: "/services/integrated-marketing-communications/",
        desc: "Five capabilities, one attribution model. Paid, organic, content, social, and AI working from a single source of truth.",
        icon: Layers,
        badge: null,
      },
      {
        label: "AI Training for Leaders",
        href: "/services/ai-training-for-leaders/",
        desc: "3-session AI crash course for executives and non-technical leaders. Practical prompting for daily business use, not theory.",
        icon: GraduationCap,
        badge: "New",
      },
      {
        label: "AI Websites",
        href: "/services/ai-websites/",
        desc: "AI-powered websites built as growth engines. Semantic HTML, Speakable schema, llms.txt, and AEO-native architecture from day one.",
        icon: Globe,
        badge: "New",
      },
      {
        label: "Google AI Mode Optimization",
        href: "/services/google-ai-mode-optimisation/",
        desc: "Get cited in Google AI Mode answers. Structured content, entity authority, and preferred-source signals that win AI Mode placement.",
        icon: Bot,
        badge: null,
      },
      {
        label: "Preferred Sources Strategy",
        href: "/services/preferred-sources-strategy/",
        desc: "Become the brand AI systems cite by default. Build the entity authority, structured data, and content depth that makes you the go-to source.",
        icon: Star,
        badge: null,
      },
      {
        label: "AI Governance & Compliance",
        href: "/services/ai-governance/",
        desc: "EU AI Act, GDPR, and regulated industry guardrails. Governance frameworks that let you move fast without regulatory risk.",
        icon: Shield,
        badge: null,
      },
      {
        label: "Performance Max Agency",
        href: "/services/performance-max-agency/",
        desc: "Specialist Performance Max campaign management. Asset group strategy, audience signals, and creative testing at scale.",
        icon: TrendingUp,
        badge: null,
      },
    ],
  },
  // Local Services
  {
    category: "London & Local Services",
    color: "#6C8EBF",
    items: [
      {
        label: "AEO Agency London",
        href: "/services/aeo-agency-london/",
        desc: "Answer Engine Optimization for London B2B. Get your brand cited in AI answers for high-intent London and UK queries.",
        icon: MapPin,
        badge: null,
      },
      {
        label: "GEO Agency London",
        href: "/services/geo-agency-london/",
        desc: "Generative Engine Optimization for London B2B. Structured content that wins citations in generative AI search results.",
        icon: Cpu,
        badge: null,
      },
      {
        label: "AI Marketing Agency London",
        href: "/services/ai-marketing-agency-london/",
        desc: "Full-stack AI growth marketing for London B2B. From strategy to execution, combining AI automation with commercial creativity.",
        icon: BookOpen,
        badge: null,
      },
      {
        label: "AI Growth Strategy (London)",
        href: "/services/ai-marketing-strategy/",
        desc: "London-based AI marketing strategy. GTM systems, ICP targeting, and AI-native demand generation for UK B2B businesses.",
        icon: Target,
        badge: null,
      },
    ],
  },
];

const serviceFaqs = [
  { q: "What services does Integrated.Social offer?", a: "Integrated.Social offers a paid AI Visibility Audit alongside AI marketing services spanning Agentic AI lead generation, AEO and GEO, PPC and Performance Max, AI website builds, account-based marketing, AI marketing strategy, integrated marketing communications, AI governance, AI training, preferred sources strategy, and SW London plans." },
  { q: "Is Integrated.Social an AI marketing agency in London?", a: "Yes. Integrated.Social is a B2B AI marketing agency based in London, UK. The agency specialises in answer engine optimisation, agentic AI systems, and AI search visibility for B2B brands. Services are delivered globally with a primary focus on the UK, US, and European markets." },
  { q: "How does Integrated.Social help brands get cited by ChatGPT and Gemini?", a: "Integrated.Social offers an evidence-led AI citation service for teams that want clearer source readiness around commercial questions. It can include answer-first pages, source context, technical delivery, structured data and entity clarity. AI systems control their own outputs, so citations are not guaranteed." },
  { q: "Do you sell an AI Visibility Audit or an AI Content Operations service?", a: "The AI Visibility Audit is a paid diagnostic service. AI Content Operations is not a standalone service; its work sits inside the live agentic, AEO and website services. The free score is separately available at /free-ai-growth-audit/." },
];

const schemaData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      "@id": "https://integrated.social/services/",
      "name": "All Services | Integrated.Social - B2B AI Marketing Agency London",
      "description": "Full list of AI marketing, AEO, GEO, PPC, and agentic AI services from Integrated.Social - B2B AI marketing agency London.",
      "url": "https://integrated.social/services/",
      "isPartOf": { "@id": "https://integrated.social/#website" },
      "about": { "@id": "https://integrated.social/#organization" },
      "author": { "@id": "https://integrated.social/modi-elnadi/#person" },
      "breadcrumb": { "@id": "https://integrated.social/services/#breadcrumb" }
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://integrated.social/services/#breadcrumb",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://integrated.social/" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://integrated.social/services/" }
      ]
    },
    {
      "@type": "FAQPage",
      "@id": "https://integrated.social/services/#faq",
      "mainEntity": serviceFaqs.map((faq) => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": { "@type": "Answer", "text": faq.a },
      })),
    }
  ]
};

export default function ServicesHub() {
  return (
    <>
      <SEOHead
        title="All Services | Integrated.Social - B2B AI Marketing Agency London"
        description="Explore AI marketing services from Integrated.Social, including a paid AI Visibility Audit, Agentic AI, SEO/AEO/GEO, PPC, AI Websites, ABM, and more. B2B-focused, London-based."
        canonical="https://integrated.social/services/"
        ogImage="https://integrated.social/og-image.jpg"
      />

      <SchemaScript schema={JSON.stringify(schemaData)} />

      <Header />

      <main className="bg-[#0F1419] min-h-screen">
        <div className="container px-4 pt-20">
          <Breadcrumbs includeSchema={false} items={[{ label: "Services" }]} />
        </div>
        {/* Hero */}
        <section className="pt-28 pb-16 px-4">
          <div className="container max-w-5xl mx-auto text-center">
            <p className="text-[#FF8C00] text-xs font-mono uppercase tracking-widest mb-4">
              B2B AI Marketing Agency London
            </p>
            <h1 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight">
              All Services
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto leading-relaxed">
              AI marketing services for B2B growth, from agentic lead generation and answer-engine citation to PPC, governance, websites and SW London plans. The AI Visibility Audit is a paid diagnostic; AI Content Operations is not a standalone service.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <a
                href="/free-ai-growth-audit/"
                className="bg-[#FF8C00] text-[#0F1419] px-6 py-3 text-sm font-semibold rounded-sm hover:bg-[#E67E00] transition-colors" onClick={() => trackSectionCtaClick({ section_name: "services_hub_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
              >
                Check my AI visibility</a>
              <a
                href="/contact/"
                className="border border-white/20 text-white/80 px-6 py-3 text-sm font-medium rounded-sm hover:border-white/40 hover:text-white transition-colors"
              >
                Talk to the Team
              </a>
            </div>
          </div>
        </section>

        {/* New AI Services spotlight banner */}
        <section className="pb-0 px-4">
          <div className="container max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-16">
              {/* Paid AI Visibility Audit spotlight */}
              <a
                href="/pricing/"
                className="group relative flex flex-col gap-4 p-6 rounded-lg border border-[#FFD700]/30 bg-[#FFD700]/[0.04] hover:bg-[#FFD700]/[0.08] hover:border-[#FFD700]/50 transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-md bg-[#FFD700]/15">
                    <ScanSearch className="w-5 h-5 text-[#FFD700]" />
                  </span>
                  <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#FFD700]/20 text-[#FFD700] self-start">
                    New Service
                  </span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white group-hover:text-[#FFD700] transition-colors mb-2">
                    AI Visibility Audit
                  </h2>
                  <p className="text-white/55 text-sm leading-relaxed">
                    A paid, senior-led diagnosis of AI-answer visibility, priority pages and the next remediation priorities.
                  </p>
                </div>
                <span className="text-xs font-medium text-[#FFD700] mt-auto">See the paid audit →</span>
              </a>

              {/* SEO, AEO and GEO spotlight */}
              <a
                href="/services/seo-aeo-geo/"
                className="group relative flex flex-col gap-4 p-6 rounded-lg border border-[#00D4AA]/30 bg-[#00D4AA]/[0.04] hover:bg-[#00D4AA]/[0.08] hover:border-[#00D4AA]/50 transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-md bg-[#00D4AA]/15">
                    <Workflow className="w-5 h-5 text-[#00D4AA]" />
                  </span>
                  <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#00D4AA]/20 text-[#00D4AA] self-start">
                    New Service
                  </span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white group-hover:text-[#00D4AA] transition-colors mb-2">
                    SEO, AEO &amp; GEO
                  </h2>
                  <p className="text-white/55 text-sm leading-relaxed">
                    Technical SEO, answer-first content and entity work for brands building a more useful AI-search presence.
                  </p>
                </div>
                <span className="text-xs font-medium text-[#00D4AA] mt-auto">Explore AI search services →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Services by category */}
        <section className="pb-20 px-4">
          <div className="container max-w-6xl mx-auto space-y-16">
            {services.map((group) => (
              <div key={group.category}>
                {/* Category header */}
                <div className="flex items-center gap-3 mb-8">
                  <span
                    className="block w-1 h-6 rounded-full"
                    style={{ background: group.color }}
                  />
                  <h2 className="text-xs font-mono uppercase tracking-widest text-white/40">
                    {group.category}
                  </h2>
                </div>

                {/* Service cards grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.items.map((service) => {
                    const Icon = service.icon;
                    return (
                      <a
                        key={service.href}
                        href={service.href}
                        className="group relative flex flex-col gap-3 p-5 bg-white/[0.03] border border-white/[0.07] rounded-lg hover:border-white/20 hover:bg-white/[0.06] transition-all duration-200"
                      >
                        {/* Badge */}
                        {service.badge && (
                          <span
                            className="absolute top-3 right-3 text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm"
                            style={{
                              background: `${group.color}20`,
                              color: group.color,
                            }}
                          >
                            {service.badge}
                          </span>
                        )}

                        {/* Icon */}
                        <span
                          className="flex items-center justify-center w-9 h-9 rounded-md"
                          style={{ background: `${group.color}15` }}
                        >
                          <Icon
                            className="w-4 h-4"
                            style={{ color: group.color }}
                          />
                        </span>

                        {/* Label */}
                        <h3
                          className="text-sm font-semibold text-white/90 group-hover:text-white transition-colors leading-snug"
                        >
                          {service.label}
                        </h3>

                        {/* Description */}
                        <p className="text-white/45 text-xs leading-relaxed flex-1">
                          {service.desc}
                        </p>

                        {/* CTA */}
                        <span
                          className="text-xs font-medium mt-1 transition-colors"
                          style={{ color: group.color }}
                        >
                          Learn more →
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="border-t border-white/5 bg-white/[0.02] px-4 py-16" aria-labelledby="services-faq-heading">
          <div className="container mx-auto max-w-4xl">
            <h2 id="services-faq-heading" className="text-center font-serif text-3xl text-white">Service questions, answered</h2>
            <FaqAccordion items={serviceFaqs} className="mt-8 space-y-3" itemClassName="overflow-hidden rounded-lg border border-white/10 bg-white/[0.03]" buttonClassName="flex w-full items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.04]" questionClassName="m-0 text-sm font-semibold text-white" answerClassName="px-5 pb-5" answerTextClassName="text-sm leading-relaxed text-white/60" iconClassName="h-5 w-5 shrink-0 text-[#FF8C00] transition-transform duration-200" />
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="border-t border-white/5 py-16 px-4">
          <div className="container max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-serif text-white mb-4">
              Not sure which service fits?
            </h2>
            <p className="text-white/50 mb-8 leading-relaxed">
              Most clients start with a free AI Visibility Score audit. In 20 minutes we identify the highest-leverage gap in your current marketing stack and recommend the right starting point.
            </p>
            <div className="overflow-x-auto mb-10 text-left border border-white/10 rounded-lg">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-white/5">
                  <tr><th className="px-4 py-3 text-white font-semibold">Question</th><th className="px-4 py-3 text-white font-semibold">Recommended Core Service</th></tr>
                </thead>
                <tbody className="divide-y divide-white/10 text-white/60">
                  <tr><td className="px-4 py-3">Who runs the agents, and what is measured first?</td><td className="px-4 py-3"><a className="text-[#FF8C00] hover:underline" href="/services/gemini-agentic-ai/">Agentic AI lead generation</a></td></tr>
                  <tr><td className="px-4 py-3">What is the difference between SEO, AEO and GEO?</td><td className="px-4 py-3"><a className="text-[#FF8C00] hover:underline" href="/services/seo-aeo-geo/">SEO, AEO and GEO</a></td></tr>
                  <tr><td className="px-4 py-3">Is the Google Ads waste a tracking problem or a Performance Max structure problem?</td><td className="px-4 py-3"><a className="text-[#FF8C00] hover:underline" href="/services/ppc-performance-max/">PPC and Performance Max</a>; for PMax only, <a className="text-[#FF8C00] hover:underline" href="/services/performance-max-agency/">Performance Max agency</a></td></tr>
                  <tr><td className="px-4 py-3">We need a strategy before a channel.</td><td className="px-4 py-3"><a className="text-[#FF8C00] hover:underline" href="/services/ai-marketing-strategy/">AI marketing strategy</a></td></tr>
                  <tr><td className="px-4 py-3">The deal dies in a buying committee.</td><td className="px-4 py-3"><a className="text-[#FF8C00] hover:underline" href="/services/account-based-marketing/">Account-based marketing</a></td></tr>
                  <tr><td className="px-4 py-3">We cannot deploy agents until legal will sign.</td><td className="px-4 py-3"><a className="text-[#FF8C00] hover:underline" href="/services/ai-governance/">AI governance</a></td></tr>
                  <tr><td className="px-4 py-3">We want Google AI Mode citations, not a blue-link report.</td><td className="px-4 py-3"><a className="text-[#FF8C00] hover:underline" href="/services/google-ai-mode-optimisation/">Google AI Mode optimisation</a></td></tr>
                  <tr><td className="px-4 py-3">We want to be the source the model cites by default.</td><td className="px-4 py-3"><a className="text-[#FF8C00] hover:underline" href="/services/preferred-sources-strategy/">Preferred sources strategy</a></td></tr>
                  <tr><td className="px-4 py-3">We need the site rebuilt for citation and leads.</td><td className="px-4 py-3"><a className="text-[#FF8C00] hover:underline" href="/services/ai-websites/">AI websites</a></td></tr>
                  <tr><td className="px-4 py-3">Leaders need to direct the tools, not watch a demo.</td><td className="px-4 py-3"><a className="text-[#FF8C00] hover:underline" href="/services/ai-training-for-leaders/">AI training for leaders</a></td></tr>
                  <tr><td className="px-4 py-3">We are paying several agencies and nobody owns the pipeline.</td><td className="px-4 py-3"><a className="text-[#FF8C00] hover:underline" href="/services/integrated-marketing-communications/">Integrated marketing communications</a></td></tr>
                  <tr><td className="px-4 py-3">A local business in SW London or Surrey wants a monthly plan.</td><td className="px-4 py-3"><a className="text-[#FF8C00] hover:underline" href="/services/ai-visibility-plan/">AI Visibility Plan</a> for the comparison. Local Presence and Full Engine are the other two rungs.</td></tr>
                </tbody>
              </table>
            </div>
            <div className="mb-10 text-left border border-white/10 rounded-lg p-6">
              <h3 className="text-white font-semibold mb-2">Do you sell an AI Visibility Audit or an AI Content Operations service?</h3>
              <p className="text-white/55 text-sm leading-relaxed">Yes: the <a href="/pricing/" className="text-[#FF8C00] hover:underline">AI Visibility Audit</a> is a paid diagnostic. The <a href="/free-ai-growth-audit/" className="text-[#FF8C00] hover:underline">AI Visibility</a> remains a separate self-serve scan. AI Content Operations is not a standalone service.</p>
            </div>
            <a
              href="/free-ai-growth-audit/"
              className="inline-block bg-[#FF8C00] text-[#0F1419] px-8 py-3.5 text-sm font-semibold rounded-sm hover:bg-[#E67E00] transition-colors" onClick={() => trackSectionCtaClick({ section_name: "services_hub_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
            >
              Check my AI visibility</a>
          </div>
        </section>
      </main>

      <Footer />
      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
